import { formatLatencyMs, prettyDateTime } from "@app/date";
import {
  fetchLlmGatewayKeyById,
  formatCurrency,
  selectLlmGatewayKeyById,
} from "@app/deploy";
import { downloadFile } from "@app/file-utils";
import {
  fetchLlmGatewayRequestRecord,
  selectLlmGatewayRequestById,
  selectLlmGatewayRequestRecord,
} from "@app/llm-gateway";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useQuery, useSelector } from "@app/react";
import {
  llmGatewayKeyRequestsUrl,
  llmGatewayUrl,
  notFoundUrl,
} from "@app/routes";
import { titleCase } from "@app/string-utils";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppSidebarLayout } from "../layouts";
import {
  Box,
  Breadcrumbs,
  DetailHeader,
  DetailInfoGrid,
  DetailInfoItem,
  DetailTitleBar,
  DropdownMenu,
  Group,
  Pill,
  PreText,
  Switch,
} from "../shared";
import { JsonView } from "../shared/json-view";

/** Recursively parses string values that hold JSON, such as response bodies. */
function parseEmbeddedJsonStrings(value: unknown): unknown {
  if (typeof value === "string") {
    const trimmed = value.trim();
    const looksLikeJson =
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"));
    if (!looksLikeJson) {
      return value;
    }

    try {
      return parseEmbeddedJsonStrings(JSON.parse(trimmed));
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) {
    return value.map(parseEmbeddedJsonStrings);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, parseEmbeddedJsonStrings(v)]),
    );
  }
  return value;
}

const NoValue = () => <span className="text-gray-500">—</span>;

const RecordMenu = ({
  viewRaw,
  onViewRawChange,
  raw,
  parsed,
  requestId,
}: {
  viewRaw: boolean;
  onViewRawChange: (value: boolean) => void;
  raw: string;
  parsed: string;
  requestId: string;
}) => (
  <DropdownMenu
    label="Options"
    items={[
      {
        id: "toggle-view",
        label: (
          <div className="flex items-center justify-between gap-4 w-full">
            <span>View Raw</span>
            <span className="pointer-events-none">
              <Switch checked={viewRaw} onChange={() => {}} />
            </span>
          </div>
        ),
        onClick: () => onViewRawChange(!viewRaw),
      },
      {
        id: "download-parsed",
        label: "Download parsed JSON",
        hasSeparator: true,
        onClick: (close) => {
          downloadFile(parsed, `request-${requestId}.json`, "application/json");
          close();
        },
      },
      {
        id: "download-raw",
        label: "Download raw JSON",
        onClick: (close) => {
          downloadFile(
            raw,
            `request-${requestId}.raw.json`,
            "application/json",
          );
          close();
        },
      },
    ]}
  />
);

const RequestRecord = ({
  record,
  requestId,
  isLoading,
  isError,
}: {
  record: string;
  requestId: string;
  isLoading: boolean;
  isError: boolean;
}) => {
  const [viewRaw, setViewRaw] = useState(false);

  const formatted = useMemo(() => {
    if (!record) return null;
    try {
      const data = JSON.parse(record);
      const parsed = parseEmbeddedJsonStrings(data);
      return {
        parsed,
        raw: JSON.stringify(data, null, 2),
        parsedText: JSON.stringify(parsed, null, 2),
      };
    } catch {
      return null;
    }
  }, [record]);

  let body = null;
  if (isLoading) {
    body = <p className="text-gray-500">Loading the request record...</p>;
  } else if (isError || !formatted) {
    body = (
      <p className="text-gray-500">
        The record for this request could not be loaded at this time.
      </p>
    );
  } else if (viewRaw) {
    body = (
      <PreText
        text={formatted.raw}
        allowCopy={true}
        className="pr-4 whitespace-pre-wrap break-all overflow-auto"
      />
    );
  } else {
    body = <JsonView src={formatted.parsed as object} />;
  }

  return (
    <Box>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Request Record</h3>
        {formatted ? (
          <RecordMenu
            viewRaw={viewRaw}
            onViewRawChange={setViewRaw}
            raw={formatted.raw}
            parsed={formatted.parsedText}
            requestId={requestId}
          />
        ) : null}
      </div>
      {body}
    </Box>
  );
};

export const LlmGatewayKeyRequestDetailPage = () => {
  const { id: llmKeyId = "", requestId = "" } = useParams();
  const orgId = useSelector(selectOrganizationSelectedId);
  const navigate = useNavigate();

  useQuery(fetchLlmGatewayKeyById({ id: llmKeyId, orgId }));
  const llmKey = useSelector((s) =>
    selectLlmGatewayKeyById(s, { id: llmKeyId }),
  );

  const loader = useQuery(
    fetchLlmGatewayRequestRecord({ orgId, llmKeyId, requestId }),
  );
  const request = useSelector((s) =>
    selectLlmGatewayRequestById(s, { id: requestId }),
  );
  const record = useSelector((s) =>
    selectLlmGatewayRequestRecord(s, { requestId }),
  );
  const isLoading = loader.isLoading || loader.isInitialLoading;
  const isHeaderLoading = isLoading && !request.id;

  useEffect(() => {
    if (!isLoading && !request.id) {
      navigate(notFoundUrl());
    }
  }, [isLoading, request.id]);

  return (
    <AppSidebarLayout>
      <Breadcrumbs
        crumbs={[
          { name: "LLM Gateway", to: llmGatewayUrl() },
          {
            name: llmKey.handle || llmKeyId,
            to: llmGatewayKeyRequestsUrl(llmKeyId),
          },
          { name: requestId, to: null },
        ]}
      />
      <Group>
        <DetailHeader>
          <DetailTitleBar
            title="Request Details"
            isLoading={isHeaderLoading}
            icon={
              <img
                src="/resource-types/logo-llm_key.png"
                className="w-[32px] h-[32px] mr-3"
                aria-label="LLM Request"
              />
            }
          />

          <DetailInfoGrid columns={3}>
            <DetailInfoItem title="Request ID" isLoading={isHeaderLoading}>
              <span className="font-mono">{request.id}</span>
            </DetailInfoItem>
            <DetailInfoItem title="Timestamp" isLoading={isHeaderLoading}>
              {prettyDateTime(request.timestamp)}
            </DetailInfoItem>
            <DetailInfoItem title="Input Tokens" isLoading={isHeaderLoading}>
              {request.inputTokens === null ? (
                <NoValue />
              ) : (
                request.inputTokens.toLocaleString()
              )}
            </DetailInfoItem>

            <DetailInfoItem title="Status" isLoading={isHeaderLoading}>
              <Pill
                variant={request.status === "success" ? "success" : "error"}
                className="capitalize"
              >
                {request.status}
              </Pill>
            </DetailInfoItem>
            <DetailInfoItem title="Operation" isLoading={isHeaderLoading}>
              {request.operation === null ? (
                <NoValue />
              ) : (
                titleCase(request.operation)
              )}
              {request.stream ? " (streamed)" : ""}
            </DetailInfoItem>
            <DetailInfoItem title="Output Tokens" isLoading={isHeaderLoading}>
              {request.outputTokens === null ? (
                <NoValue />
              ) : (
                request.outputTokens.toLocaleString()
              )}
            </DetailInfoItem>

            <DetailInfoItem title="Model" isLoading={isHeaderLoading}>
              {request.model === null ? (
                <NoValue />
              ) : (
                <span className="font-mono">{request.model}</span>
              )}
            </DetailInfoItem>
            <DetailInfoItem title="Latency" isLoading={isHeaderLoading}>
              {formatLatencyMs(request.latencyMs)}
            </DetailInfoItem>
            <DetailInfoItem title="Cost" isLoading={isHeaderLoading}>
              {request.cost === null ? (
                <NoValue />
              ) : (
                formatCurrency(request.cost, 4)
              )}
            </DetailInfoItem>
          </DetailInfoGrid>
        </DetailHeader>

        <RequestRecord
          record={record}
          requestId={requestId}
          isLoading={isLoading}
          isError={loader.isError}
        />
      </Group>
    </AppSidebarLayout>
  );
};
